(() => {
  const namespace = 'ArmourReviewUI';

  const normalize = value => String(value || '')
    .toLocaleLowerCase('en-IN')
    .replace(/[\u2012-\u2015]/g, '-')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const medalLoads = new WeakSet();

  const medalAssetURL = image => {
    if (!image) return null;
    const path = image.dataset.url || '';
    if (!/^[a-z0-9_-]+\/[a-z0-9_.-]+\.svg$/i.test(path)) return null;

    const publicRoot = window.jdgm?.JM_PUBLIC_IMAGE_URL
      || 'https://public-images.judge.me/judgeme/';
    let root;
    try {
      root = new URL(publicRoot);
    } catch {
      return null;
    }
    if (root.protocol !== 'https:' || root.hostname !== 'public-images.judge.me') return null;

    const collection = window.jdgm?.WIDGET_REBRANDING_ENABLED
      ? 'medals-v2-2025-rebranding/'
      : 'medals-v2/';
    const url = new URL(`${collection}${path}`, root);
    url.searchParams.set('auto', 'format');
    return url.href;
  };

  const medalLabel = medal => {
    const title = medal.closest('.jdgm-medal-wrapper')?.getAttribute('title') || '';
    return title.split('.')[0].trim()
      || (medal.getAttribute('aria-label') || '')
        .replace(/^Judge\.me\s+/i, '')
        .replace(/\s+medal$/i, '')
        .trim();
  };

  const loadMedal = medal => new Promise(resolve => {
    const imageHost = medal.querySelector('.jdgm-medal__image[data-url]');
    if (!imageHost) return resolve(false);

    const src = medalAssetURL(imageHost);
    const alt = imageHost.dataset.alt || medal.getAttribute('aria-label') || '';
    if (!src || !alt.startsWith('Judge.me ')) return resolve(false);

    const existing = imageHost.querySelector('img[src]');
    if (existing) {
      try {
        const actual = new URL(existing.currentSrc || existing.src);
        const expected = new URL(src);
        if (
          actual.origin === expected.origin
          && actual.pathname === expected.pathname
          && existing.complete
          && existing.naturalWidth > 0
        ) {
          medal.classList.remove('jdgm--loading');
          return resolve(true);
        }
      } catch {
        /* Replace an invalid or non-URL child with the validated vendor asset. */
      }
    }

    const image = new Image(72, 72);
    let settled = false;
    const finish = success => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      if (success) {
        medal.classList.remove('jdgm--loading');
      } else {
        image.remove();
      }
      resolve(success);
    };
    const timeout = setTimeout(() => finish(false), 6000);

    image.alt = alt;
    image.decoding = 'async';
    image.loading = 'eager';
    image.addEventListener('load', () => finish(image.naturalWidth > 0), { once: true });
    image.addEventListener('error', () => finish(false), { once: true });
    image.src = src;
    imageHost.replaceChildren(image);
    if (image.complete) finish(image.naturalWidth > 0);
  });

  const hydrateMedals = root => {
    const wrappers = [];
    if (root?.matches?.('.jdgm-medals-wrapper')) wrappers.push(root);
    root?.querySelectorAll?.('.jdgm-medals-wrapper').forEach(wrapper => wrappers.push(wrapper));
    if (!wrappers.length) return;

    const allWrappers = [...document.querySelectorAll('.jdgm-medals-wrapper')];
    const preferred = allWrappers.find(wrapper => wrapper.closest('.ag-review-awards'))
      || allWrappers[0];

    allWrappers.forEach(wrapper => {
      if (wrapper === preferred) return;
      wrapper.classList.remove('ag-review-medals-ready');
      wrapper.classList.add('jdgm-hidden');
      wrapper.setAttribute('aria-hidden', 'true');
    });

    if (!preferred || medalLoads.has(preferred)) return;
    const medals = [...preferred.querySelectorAll('.jdgm-medal')];
    const hasOfficialEntitlements = preferred.dataset.updatedAt
      && medals.length > 0
      && medals.every(medal => (
        medal.dataset.type
        && medal.dataset.tier
        && medal.getAttribute('aria-label')?.startsWith('Judge.me ')
        && medalAssetURL(medal.querySelector('.jdgm-medal__image[data-url]'))
      ));
    if (!hasOfficialEntitlements) return;

    medalLoads.add(preferred);
    medals.forEach(medal => {
      if (medal.querySelector('.ag-review-medal-label')) return;
      const label = medalLabel(medal);
      if (!label) return;
      const text = document.createElement('span');
      text.className = 'ag-review-medal-label';
      text.textContent = label;
      medal.append(text);
    });

    Promise.all(medals.map(loadMedal)).then(results => {
      if (!results.every(Boolean)) return;
      const verified = preferred.querySelector('.jdgm-verified-wrapper');
      if (verified && !verified.textContent.trim()) verified.hidden = true;
      preferred.classList.remove('jdgm-hidden');
      preferred.classList.add('ag-review-medals-ready');
      preferred.removeAttribute('aria-hidden');
      preferred.setAttribute('role', 'region');
      preferred.setAttribute('aria-label', 'Judge.me store awards');
    });
  };

  const addAggregateScore = widget => {
    const summary = widget.querySelector('.jdgm-rev-widg__summary');
    const stars = summary?.querySelector('.jdgm-rev-widg__summary-stars');
    if (!summary || !stars) return;

    const match = (stars.getAttribute('aria-label') || '').match(/([0-5](?:\.\d+)?)/);
    if (!match) return;

    if (!summary.querySelector('.ag-review-score')) {
      const score = document.createElement('strong');
      score.className = 'ag-review-score';
      score.textContent = `${Number(match[1]).toFixed(1)} out of 5`;
      stars.insertAdjacentElement('afterend', score);
    }

    if (summary.querySelector('.ag-review-count')) return;
    const count = [...widget.querySelectorAll('.jdgm-histogram__row[data-frequency]')]
      .reduce((total, row) => total + Number(row.dataset.frequency || 0), 0);
    if (!count) return;

    const countLabel = document.createElement('span');
    countLabel.className = 'ag-review-count';
    countLabel.textContent = `${count} reviews`;
    summary.append(countLabel);
  };

  const markRatingOnlyReviews = widget => {
    widget.querySelectorAll('.jdgm-rev').forEach(review => {
      const title = normalize(review.querySelector('.jdgm-rev__title')?.textContent);
      const body = review.querySelector('.jdgm-rev__body');
      const bodyText = normalize(body?.textContent);
      const productTitle = normalize(review.dataset.productTitle);
      const isRatingOnly = !title && (!bodyText || bodyText === productTitle);
      const existingNote = review.querySelector('.ag-review-rating-only-note');
      const actions = review.querySelector('.jdgm-rev__actions');

      review.classList.toggle('ag-review-rating-only', isRatingOnly);
      actions?.classList.toggle(
        'ag-review-actions-empty',
        !actions.querySelector('a, button, [role="button"]')
      );
      if (!isRatingOnly) {
        existingNote?.remove();
        return;
      }
      if (existingNote) return;

      const note = document.createElement('p');
      note.className = 'ag-review-rating-only-note';
      note.textContent = 'Rating only';
      body?.insertAdjacentElement('afterend', note);
    });
  };

  const enhance = widget => {
    addAggregateScore(widget);
    markRatingOnlyReviews(widget);
    widget.classList.add('ag-review-ui-ready');
  };

  const refresh = root => {
    if (root?.matches?.('.jdgm-review-widget')) enhance(root);
    root?.querySelectorAll?.('.jdgm-review-widget').forEach(enhance);
    hydrateMedals(root);
  };

  if (window[namespace]) {
    window[namespace].refresh(document);
    return;
  }

  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      refresh(document);
    });
  });

  window[namespace] = { refresh };
  refresh(document);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
